/**
 * MonthlyScheduleEditor · Sprint 3.1 · v2.8.5
 *
 * Editor visual mes-por-mes para calendarios `[{month, amount}]`:
 *  - custom_ministration_json  (Ministraciones del banco)
 *  - equity_schedule_json      (Aportes de capital declarados)
 *  - bridge_repayment_schedule_json (Pagos manuales del puente)
 *
 * Reemplaza los textareas JSON con:
 *  - Tabla editable inline (mes, monto)
 *  - Validaciones en vivo (mes ≥ 1, monto ≥ 0, duplicados advertidos)
 *  - Total en footer + comparación opcional con `sumTarget`
 *  - Toggle "Ver JSON" para power users que prefieran pegar
 *  - Guarda como string JSON compacto (drop-in con el motor)
 */
import { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, Code2, Table as TableIcon, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

function parseInitial(value) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(r => ({
        month: parseInt(r?.month) || 0,
        amount: parseFloat(r?.amount) || 0,
      }))
      .filter(r => r.month > 0);
  } catch {
    return [];
  }
}

function toJsonString(rows) {
  const clean = rows
    .filter(r => (parseInt(r.month) || 0) > 0 && (parseFloat(r.amount) || 0) > 0)
    .map(r => ({ month: parseInt(r.month), amount: parseFloat(r.amount) }))
    .sort((a, b) => a.month - b.month);
  return clean.length === 0 ? '' : JSON.stringify(clean);
}

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function MonthlyScheduleEditor({
  value,
  onChange,
  label,
  helpText,
  maxMonth = 60,
  sumTarget = 0,
  sumTargetLabel = 'meta',
  moneyLabel = 'Monto',
  testId = 'monthly-schedule',
  color = '#4F46E5',
  bg = 'rgba(79,70,229,0.04)',
}) {
  const [rows, setRows] = useState(() => parseInitial(value));
  const [jsonMode, setJsonMode] = useState(false);
  const [rawJson, setRawJson] = useState(() => value || '');
  const [jsonError, setJsonError] = useState('');

  // Re-sync when parent value changes externally (e.g., scenario switch)
  useEffect(() => {
    if (!jsonMode) {
      setRows(parseInitial(value));
    }
    setRawJson(value || '');
  }, [value, jsonMode]);

  const total = useMemo(() => rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0), [rows]);
  const duplicates = useMemo(() => {
    const seen = new Set();
    const dupes = new Set();
    rows.forEach(r => {
      const m = parseInt(r.month) || 0;
      if (m > 0) {
        if (seen.has(m)) dupes.add(m);
        seen.add(m);
      }
    });
    return dupes;
  }, [rows]);
  const outOfRange = useMemo(() => rows.filter(r => (parseInt(r.month) || 0) > maxMonth), [rows, maxMonth]);
  const progress = sumTarget > 0 ? Math.min(100, (total / sumTarget) * 100) : 0;

  const commit = (nextRows) => {
    setRows(nextRows);
    const json = toJsonString(nextRows);
    onChange?.(json);
    setRawJson(json);
  };

  const addRow = () => {
    const nextMonth = rows.length === 0 ? 1 : Math.max(...rows.map(r => parseInt(r.month) || 0)) + 1;
    commit([...rows, { month: nextMonth, amount: 0 }]);
  };

  const updateRow = (idx, key, v) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [key]: v } : r));
    commit(next);
  };

  const removeRow = (idx) => commit(rows.filter((_, i) => i !== idx));

  const applyRawJson = () => {
    setJsonError('');
    if (!rawJson.trim()) {
      commit([]);
      setJsonMode(false);
      return;
    }
    try {
      const parsed = JSON.parse(rawJson);
      if (!Array.isArray(parsed)) throw new Error('El JSON debe ser un array');
      const clean = parsed
        .map(r => ({ month: parseInt(r?.month) || 0, amount: parseFloat(r?.amount) || 0 }))
        .filter(r => r.month > 0);
      commit(clean);
      setJsonMode(false);
    } catch (e) {
      setJsonError(e.message || 'JSON inválido');
    }
  };

  return (
    <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: `${color}55`, background: bg }} data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color }}>
            {label}
          </div>
          {helpText && <div className="text-[10px] mt-0.5" style={{ color: '#64748B' }}>{helpText}</div>}
        </div>
        <div className="flex gap-1 shrink-0">
          <Button
            size="sm" variant="ghost"
            className="h-7 px-2 text-[10px]"
            onClick={() => setJsonMode(v => !v)}
            data-testid={`${testId}-toggle-json`}
            title={jsonMode ? 'Volver a tabla' : 'Ver / pegar JSON'}
          >
            {jsonMode ? <TableIcon className="w-3 h-3 mr-1" /> : <Code2 className="w-3 h-3 mr-1" />}
            {jsonMode ? 'Tabla' : 'JSON'}
          </Button>
        </div>
      </div>

      {jsonMode ? (
        <div className="space-y-2">
          <textarea
            className="w-full text-[11px] font-mono p-2 rounded border"
            style={{ borderColor: '#CBD5E1', minHeight: '90px' }}
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            placeholder='[{"month":1,"amount":5000000},{"month":4,"amount":5000000}]'
            data-testid={`${testId}-json-input`}
          />
          {jsonError && (
            <div className="text-[10px] flex items-center gap-1" style={{ color: '#DC2626' }}>
              <AlertTriangle className="w-3 h-3" />{jsonError}
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={applyRawJson} data-testid={`${testId}-apply-json`} style={{ background: color, color: 'white' }}>
              Aplicar JSON
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setRawJson(value || ''); setJsonError(''); setJsonMode(false); }}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <>
          {rows.length === 0 ? (
            <div className="text-[11px] text-center py-4 rounded" style={{ background: 'rgba(148,163,184,0.08)', color: '#64748B' }}>
              Sin filas capturadas. <button className="underline" onClick={addRow} data-testid={`${testId}-empty-add`}>Agregar la primera</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid={`${testId}-table`}>
                <thead>
                  <tr className="border-b" style={{ borderColor: 'rgba(148,163,184,0.25)' }}>
                    <th className="text-left py-1.5 px-2 font-semibold" style={{ color: '#64748B', width: '25%' }}>Mes</th>
                    <th className="text-right py-1.5 px-2 font-semibold" style={{ color: '#64748B' }}>{moneyLabel}</th>
                    <th className="text-right py-1.5 px-2 font-semibold" style={{ color: '#64748B', width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const m = parseInt(r.month) || 0;
                    const isDup = duplicates.has(m);
                    const isOut = m > maxMonth;
                    const hasError = isDup || isOut || m <= 0;
                    return (
                      <tr key={idx} className="border-b" style={{ borderColor: 'rgba(148,163,184,0.15)' }}>
                        <td className="py-1 px-2">
                          <Input
                            type="number"
                            min={1}
                            max={maxMonth}
                            value={r.month}
                            onChange={(e) => updateRow(idx, 'month', e.target.value)}
                            className="h-7 text-[11px]"
                            style={hasError ? { borderColor: '#EF4444' } : {}}
                            data-testid={`${testId}-month-${idx}`}
                          />
                          {isDup && <div className="text-[9px]" style={{ color: '#EF4444' }}>duplicado</div>}
                          {isOut && <div className="text-[9px]" style={{ color: '#EF4444' }}>&gt; {maxMonth}</div>}
                        </td>
                        <td className="py-1 px-2 text-right">
                          <Input
                            type="number"
                            min={0}
                            step={1000}
                            value={r.amount}
                            onChange={(e) => updateRow(idx, 'amount', e.target.value)}
                            className="h-7 text-[11px] text-right font-mono"
                            data-testid={`${testId}-amount-${idx}`}
                          />
                        </td>
                        <td className="py-1 px-1 text-right">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => removeRow(idx)} data-testid={`${testId}-del-${idx}`}>
                            <Trash2 className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="pt-2 px-2 font-semibold text-[11px]" style={{ color: '#334155' }}>Total</td>
                    <td className="pt-2 px-2 text-right font-semibold tabular-nums text-[11px]" style={{ color }} data-testid={`${testId}-total`}>
                      {fmtMoney(total)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {sumTarget > 0 && (
            <div className="text-[10px] flex items-center justify-between gap-2 mt-2" style={{ color: '#64748B' }}>
              <div>{sumTargetLabel}: <strong style={{ color: '#334155' }}>{fmtMoney(sumTarget)}</strong></div>
              <div style={{ color: total > sumTarget * 1.001 ? '#DC2626' : total > sumTarget * 0.999 ? '#16A34A' : '#64748B' }}>
                {progress.toFixed(1)}% cubierto
                {total > sumTarget * 1.001 && ' · excede meta'}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-2">
            <Button size="sm" variant="ghost" onClick={addRow} data-testid={`${testId}-add`} className="text-[11px] h-7">
              <Plus className="w-3 h-3 mr-1" /> Agregar fila
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
